import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactQuill from "react-quill"
import { enqueueSnackbar } from "notistack"
import {
  CustomEditorFooter,
  CustomEditorToolbar,
  StyledBox
} from "./components"
import { DEFAULT_TEXT_LENGTH, TEXT_EDITOR_ATTRIBUTES } from "../../../constants"
import {
  copyText,
  cutText,
  editorFormats,
  moveFocusToNextElement,
  pasteText,
  reDo,
  unDo
} from "./utils"
import { SpecialCharactersModal } from "../../modals/SpecialCharactersModal"
import { TextFormatType } from "../../../types"
import { Box, Button, Typography } from "@mui/material"

interface TextEditorComponentProps {
  value: string
  changeHandler: (text: string, xhtml: string, lines: number) => void
  placeholder: string
  isDisabled: boolean
  expectedLength?: number
  expectedLengthDisplay?: "percentage" | "count"
  format?: TextFormatType
  onSubmit?: () => void
  showSubmit?: boolean
  error?: string
  editorId?: string
}

export const TextEditorComponent: FC<TextEditorComponentProps> = props => {
  const {
    value,
    changeHandler,
    placeholder,
    isDisabled,
    expectedLength = DEFAULT_TEXT_LENGTH,
    expectedLengthDisplay = "count",
    format = TextFormatType.xhtml,
    showSubmit,
    onSubmit,
    error,
    editorId = "text-editor",
    ...accessibilityAttr
  } = props
  const isRichText = format === TextFormatType.xhtml
  const isPercentage = expectedLengthDisplay === "percentage"
  const [openCharactersModal, setOpenCharactersModal] = useState<boolean>(false)
  const [currentLength, setCurrentLength] = useState(() =>
    value ? value.replace(/<[^>]*>/g, "").length : 0
  )

  const remainingPercent =
    expectedLength > 0
      ? Math.max(
          0,
          Math.ceil(((expectedLength - currentLength) / expectedLength) * 100)
        )
      : 0
  const isAtLimit = currentLength >= expectedLength
  const quillRef = useRef<ReactQuill>()
  // Prevents false paste warnings when navigating back to a page with existing text, which Quill treats as a paste-like insert
  const isPasteRef = useRef(false) // track user pasting for snackbar truncation limit warning
  const characterCountId = `${editorId}-character-count`

  const editorModules = useMemo(
    () => ({
      toolbar: isRichText
        ? {
            container: "#toolbar",
            handlers: {
              cut: () => cutText(quillRef.current?.getEditor()),
              copy: () => copyText(quillRef.current?.getEditor()),
              paste: () => {
                isPasteRef.current = true
                pasteText(quillRef.current?.getEditor())
              },
              undo: () => unDo(quillRef.current?.getEditor()),
              redo: () => reDo(quillRef.current?.getEditor())
            }
          }
        : false,
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: () => moveFocusToNextElement(quillRef.current?.getEditor())
          }
        }
      },
      history: {
        delay: 500,
        maxStack: 100
      }
    }),
    [isRichText]
  )

  const handleOpenCharactersModal = () => {
    setOpenCharactersModal(true)
  }

  const handleCloseCharactersModal = () => {
    setOpenCharactersModal(false)
  }

  const insertSpecialCharacter = useCallback((character: string) => {
    const editor: any = quillRef.current?.getEditor()
    const cursorIndex = editor?.selection?.savedRange?.index
    editor.insertText(cursorIndex || 0, character)
  }, [])

  useEffect(() => {
    if (quillRef.current) {
      setCurrentLength(quillRef.current.getEditor().getLength() - 1)
    }
  }, [value])

  useEffect(() => {
    quillRef.current?.getEditor().enable(!isDisabled)
  }, [isDisabled])

  // Block keyboard input when at the character limit
  // Allow navigation, deletion, selection, and shortcuts
  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    const editorRoot = editor.root
    const handleKeyDown = (e: KeyboardEvent) => {
      const length = editor.getLength() - 1
      if (length < expectedLength) return

      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
        "Escape"
      ]
      if (allowedKeys.includes(e.key)) return
      if (e.ctrlKey || e.metaKey) return
      if (e.key.length > 1) return

      e.preventDefault()
    }

    editorRoot.addEventListener("keydown", handleKeyDown)
    return () => editorRoot.removeEventListener("keydown", handleKeyDown)
  }, [expectedLength])

  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    editor.keyboard.addBinding(
      {
        key: "V",
        shortKey: true
      },
      () => {
        isPasteRef.current = true
        pasteText(quillRef.current?.getEditor())
      }
    )

    editor.keyboard.addBinding(
      {
        key: "C",
        shortKey: true
      },
      () => copyText(quillRef.current?.getEditor())
    )

    // Apply accessibility attributes to the actual editor element
    const editorRoot = editor.root

    editorRoot.setAttribute("spellcheck", "false")
    setCurrentLength(quillRef.current.getEditor().getLength() - 1)
    // Associate the editor with the character count via aria-describedby
    const existingDescribedBy = editorRoot.getAttribute("aria-describedby")

    // Apply aria-label if provided
    if (accessibilityAttr["aria-label"]) {
      editorRoot.setAttribute("aria-label", accessibilityAttr["aria-label"])
    }

    // Apply aria-labelledby if provided
    if (accessibilityAttr["aria-labelledby"]) {
      editorRoot.setAttribute(
        "aria-labelledby",
        accessibilityAttr["aria-labelledby"]
      )
    }

    // If no label is provided, set a default accessible name
    if (
      !accessibilityAttr["aria-label"] &&
      !accessibilityAttr["aria-labelledby"]
    ) {
      editorRoot.setAttribute("aria-label", "Text editor")
    }

    // Apply role if provided
    if (accessibilityAttr["role"]) {
      editorRoot.setAttribute("role", accessibilityAttr["role"])
    }

    // Apply aria-multiline if provided
    if (accessibilityAttr["aria-multiline"]) {
      editorRoot.setAttribute(
        "aria-multiline",
        accessibilityAttr["aria-multiline"]
      )
    }

    if (existingDescribedBy) {
      // If there's already an aria-describedby, append to it
      if (!existingDescribedBy.includes(characterCountId)) {
        editorRoot.setAttribute(
          "aria-describedby",
          `${existingDescribedBy} ${characterCountId}`
        )
      }
    } else {
      editorRoot.setAttribute("aria-describedby", characterCountId)
    }
  }, [accessibilityAttr, characterCountId])

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" })
    }
  }, [error])

  const quillEditor = useMemo(() => {
    return (
      <ReactQuill
        theme="snow"
        modules={editorModules}
        formats={isRichText ? editorFormats : []}
        value={value}
        onChange={(_value, _delta, _source, editor) => {
          if (editor.getLength() > expectedLength) {
            const textLength = editor.getLength() - 1
            const willTruncate = textLength > expectedLength
            if (willTruncate && isPasteRef.current) {
              enqueueSnackbar(
                "Pasted text was truncated. Please check your answer before submission.",
                { variant: "warning", autoHideDuration: 5000 }
              )
            }
            isPasteRef.current = false
            quillRef.current
              ?.getEditor()
              .deleteText(expectedLength, editor.getLength())
          }
          const text = editor.getText(0, editor.getLength())
          const html = text.trim() ? editor.getHTML() : ""
          setCurrentLength(editor.getLength() - 1)
          changeHandler(text, html, text.split("\n").length - 1)
        }}
        placeholder={placeholder}
        ref={quillRef}
      />
    )
  }, [
    editorModules,
    editorFormats,
    value,
    changeHandler,
    placeholder,
    expectedLength,
    quillRef
  ])

  return (
    <>
      <StyledBox
        className="text-editor"
        {...TEXT_EDITOR_ATTRIBUTES}
        {...accessibilityAttr}
      >
        {isPercentage && (
          <Typography
            variant="body2"
            sx={{
              color: isAtLimit ? "orange.700" : undefined,
              marginBottom: "0 !important",
              paddingBottom: 2
            }}
          >
            {`Space remaining: ${remainingPercent}%`}
          </Typography>
        )}
        {isRichText && (
          <CustomEditorToolbar
            disabled={isDisabled}
            handleOpenCharactersModal={handleOpenCharactersModal}
          />
        )}
        {quillEditor}
        <CustomEditorFooter
          textLength={currentLength}
          maxTextLength={expectedLength}
          displayStyle={expectedLengthDisplay}
          isAtLimit={isAtLimit}
          characterCountId={characterCountId}
        />
      </StyledBox>

      <SpecialCharactersModal
        open={openCharactersModal}
        onClose={handleCloseCharactersModal}
        insertSpecialCharacter={insertSpecialCharacter}
      />

      {!!showSubmit && (
        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            // Disable the submit button if user does not provide text
            disabled={isDisabled || currentLength === 0}
            variant="outlined"
            size="medium"
            color="grey"
            onClick={() => onSubmit()}
          >
            Submit
          </Button>
        </Box>
      )}
    </>
  )
}
